<template>
  <v-progress-circular
    v-if="renderedDescription === undefined"
    indeterminate
    color="primary"
    :aria-label="t(l.label_loading)"
  ></v-progress-circular>
  <!-- eslint-disable-next-line vue/no-v-html -->
  <div v-else class="markdown" v-html="renderedDescription"></div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import { marked, Token, TokenizerAndRendererExtension } from 'marked';
import isArray from 'lodash/isArray';
import useKeywordsStore from '@/store/keywords';
import useLanguagesStore from '@/store/languages';
import { useLocale } from '@/plugins/translation';

const { t, l } = useLocale();

const { getByKey, loadByKey } = useKeywordsStore();
const { getMarketLanguage } = useLanguagesStore();

const props = defineProps<{
  description: string | undefined;
  supportLanguage?: string;
}>();

const renderedDescription = ref<string | undefined>();

const keywordShortDescription = async (kw: string) => {
  let keyword = getByKey(kw) || getByKey(kw.toLowerCase());
  if (!keyword) {
    keyword = await loadByKey(kw);
  }
  return keyword?.translations.description[props.supportLanguage || getMarketLanguage()]?.translation;
};

/*
 * Custom marked.js extension overriding Markdown link parsing to be able to handle both links of form [link] and [link](<keyword to link>)
 * https://marked.js.org/using_pro#extensions
 * */
const keywordLink: TokenizerAndRendererExtension = {
  name: 'keywordLink',
  level: 'inline',
  start(src: string) {
    return src.match(/\[/)?.index;
  },

  tokenizer(src: string) {
    const rule = /^\[([^\]]+)](?:\(([^\]]+)\))?/; // Regex for the complete token, must anchor to string start
    const match = rule.exec(src);
    if (match) {
      return {
        type: 'keywordLink', // Should match "name" above
        raw: match[0], // Text to consume from the source
        text: match[2]?.trim() || match[1].trim(), // Additional custom properties
        display: match[1],
      };
    }
    return undefined;
  },
  renderer(token) {
    return token.html;
  },
};

const renderMarkdown = async (value: string | string[] | undefined) => {
  if (!value || isArray(value)) {
    console.log('No description to render', value);
    renderedDescription.value = '';
    return;
  }
  marked.use({
    extensions: [keywordLink],
    async: true,
    async walkTokens(token: Token) {
      if (token.type === 'keywordLink') {
        const description = await keywordShortDescription(token.text);
        if (description) {
          token.html = `<span class="tooltip" data-tooltip="${description}">${token.display}</span>`;
        } else {
          token.html = token.display;
        }
      }
    },
  });
  renderedDescription.value = await marked.parse(value);
};

watchEffect(() => {
  renderMarkdown(props.description);
});
</script>

<style lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';
.markdown p {
  margin-bottom: 0.8em;
}

.markdown ul,
.markdown ol {
  padding-inline: 1.5rem;
  margin-bottom: 1rem;
}

.tooltip {
  position: relative;
  text-decoration: underline dashed;
  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    text-decoration: none;
    font-weight: bold;
  }
}

.tooltip {
  &:hover::after {
    opacity: 1;
  }

  &::after {
    font-weight: normal;
    font-family: settings.$body-font-family;
    font-style: normal;
    text-decoration: none;
    content: attr(data-tooltip);
    border-radius: 4px;
    opacity: 0;
    font-size: 1rem;
    position: absolute;
    bottom: 100%;
    left: 50%;
    min-width: 10rem;
    transform: translateX(-50%);
    background-color: rgb(var(--v-theme-surface-variant));
    color: rgb(var(--v-theme-on-surface-variant));
    text-align: center;
    transition: all 0.3s ease-in;
    padding: 8px 16px;
    margin-bottom: 8px;
    pointer-events: none;
  }
}
</style>
